import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Loader2, FolderKanban, Calendar, User, Trash2, LayoutGrid, Link2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Project name is required"); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const project = await res.json();
        toast.success("Project created");
        setDialogOpen(false);
        setForm({ name: "", description: "" });
        navigate(`/projects/${project.project_id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create project");
      }
    } catch (e) {
      toast.error("Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project and all its cards?")) return;
    try {
      const res = await fetch(`${API}/projects/${projectId}`, {
        method: "DELETE", credentials: "include"
      });
      if (res.ok) {
        toast.success("Project deleted");
        fetchProjects();
      }
    } catch (e) {
      toast.error("Failed to delete project");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FolderKanban className="w-7 h-7 text-primary" />
            Projects
          </h1>
          <p className="text-slate-500 mt-1">{projects.length} project{projects.length !== 1 ? "s" : ""}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Project Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Q2 Sales Campaign"
                  data-testid="project-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief project description..."
                  rows={3}
                  data-testid="project-desc-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleCreate} disabled={creating} data-testid="submit-project-btn">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {projects.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FolderKanban className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-1">No projects yet</h3>
            <p className="text-sm text-slate-400 mb-4">Create your first project board</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <Card
              key={p.project_id}
              className="cursor-pointer hover:border-primary/40 hover:shadow-md transition-all group"
              onClick={() => navigate(`/projects/${p.project_id}`)}
              data-testid={`project-card-${p.project_id}`}
            >
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800 truncate">{p.name}</h3>
                    {p.description && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{p.description}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                    onClick={(e) => handleDelete(e, p.project_id)}
                    data-testid={`delete-project-${p.project_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  <Badge variant="outline" className="text-xs gap-1">
                    <LayoutGrid className="w-3 h-3" />
                    {p.total_cards || 0} cards
                  </Badge>
                  {p.lead_name && (
                    <Badge variant="outline" className="text-xs gap-1">
                      <Link2 className="w-3 h-3" />
                      {p.lead_name}
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-xs gap-1 text-slate-400">
                    <Calendar className="w-3 h-3" />
                    {new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </Badge>
                </div>

                {/* Column previews */}
                <div className="flex gap-1 mt-3">
                  {(p.columns || []).slice(0, 5).map((col) => (
                    <div key={col.column_id} className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: col.color + "40" }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          backgroundColor: col.color,
                          width: p.total_cards > 0
                            ? `${Math.min(100, ((p.card_counts?.[col.column_id] || 0) / p.total_cards) * 100)}%`
                            : "0%"
                        }}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
